import React, { useState, useEffect, useRef, useMemo } from 'react';
import { IoClose } from 'react-icons/io5';
import { FiUpload, FiFile, FiTrash2, FiLink } from 'react-icons/fi';
import JoditEditor from 'jodit-react';
import fnc from '../mymod/commFunction';
import './MaualAdmin.scss';

const MaualAdmin = ({ selectedCategory, selectedItem, onClose, onSave }) => {
    const editor = useRef(null);
    const [mode, setMode] = useState('list'); // list, create, edit
    const [list, setList] = useState([]);
    const [formData, setFormData] = useState({
        sno: null,
        pCode: '',
        unit2_code: '',
        unit2_name: '',
        content: '',
        sort_order: 0,
        useyn: 'Y'
    });
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null);

    const S3_HOSTS = new Set([
        'mathautogen.s3.ap-northeast-2.amazonaws.com',
        's3.ap-northeast-2.amazonaws.com'
    ]);

    const extractS3KeyFromSrc = (src) => {
        if (!src) return '';
        const trimmed = String(src).trim();
        if (!trimmed) return '';

        const directKey = trimmed.replace(/^\/+/, '');
        if (/^(mathtailer\/|web\/|public\/)/i.test(directKey)) {
            return directKey;
        }

        if (!/^https?:\/\//i.test(trimmed)) return '';

        try {
            const parsed = new URL(trimmed);
            const host = String(parsed.hostname || '').toLowerCase();
            const isS3Host =
                S3_HOSTS.has(host) ||
                host.endsWith('.s3.ap-northeast-2.amazonaws.com');

            if (!isS3Host) return '';

            const keyPath = decodeURIComponent((parsed.pathname || '').replace(/^\/+/, ''));
            return keyPath;
        } catch (error) {
            return '';
        }
    };

    const normalizeContentImageSrcToS3Path = (html) => {
        if (!html) return '';

        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const imgNodes = Array.from(doc.querySelectorAll('img[src]'));

            imgNodes.forEach((node) => {
                const src = node.getAttribute('src') || '';
                const s3Key = extractS3KeyFromSrc(src);
                if (s3Key) {
                    node.setAttribute('src', s3Key);
                }
            });

            return doc.body.innerHTML;
        } catch (error) {
            return html;
        }
    };

    const requestSignedImageUrl = async (imagePath) => {
        const response = await fetch(`${fnc.svrUrl}/s3Routes/get-image-path`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imagePath, sec: 600 })
        });

        if (!response.ok) return '';
        const data = await response.json();
        return data?.url || '';
    };

    const normalizeContentImageSrcToSignedUrlAsync = async (html) => {
        if (!html) return '';

        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const imgNodes = Array.from(doc.querySelectorAll('img[src]'));
            const cache = new Map();

            await Promise.all(
                imgNodes.map(async (node) => {
                    const src = node.getAttribute('src') || '';
                    const s3Key = extractS3KeyFromSrc(src);
                    if (!s3Key) return;

                    if (!cache.has(s3Key)) {
                        cache.set(s3Key, requestSignedImageUrl(s3Key));
                    }

                    const signedUrl = await cache.get(s3Key);
                    if (signedUrl) {
                        node.setAttribute('src', signedUrl);
                    }
                })
            );

            return doc.body.innerHTML;
        } catch (error) {
            return html;
        }
    };

    const extFromMimeType = (mimeType) => {
        const value = String(mimeType || '').toLowerCase();
        if (value.includes('png')) return 'png';
        if (value.includes('jpeg') || value.includes('jpg')) return 'jpg';
        if (value.includes('gif')) return 'gif';
        if (value.includes('webp')) return 'webp';
        if (value.includes('bmp')) return 'bmp';
        return 'png';
    };

    const uploadDataImageToS3 = async (dataUrl, index = 0) => {
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        const ext = extFromMimeType(blob.type);
        const now = Date.now();
        const safeUnitCode = formData.unit2_code || `manual_${now}`;
        const fileName = `${safeUnitCode}_${now}_${index}.${ext}`;
        const s3Key = `mathtailer/board/images/${fileName}`;

        const uploadFormData = new FormData();
        uploadFormData.append('file', new File([blob], fileName, { type: blob.type || `image/${ext}` }));
        uploadFormData.append('s3Key', s3Key);

        const uploadRes = await fetch(`${fnc.svrUrl}/s3Routes/uploadS3`, {
            method: 'POST',
            body: uploadFormData
        });

        if (!uploadRes.ok) {
            throw new Error('S3 image upload failed');
        }

        const uploadJson = await uploadRes.json();
        if (!uploadJson?.success) {
            throw new Error(uploadJson?.error || 'S3 image upload failed');
        }

        return s3Key;
    };

    const uploadExternalImageToS3 = async (url, index = 0) => {
        const now = Date.now();
        const safeUnitCode = formData.unit2_code || `manual_${now}`;
        const urlPath = url.split('?')[0];
        const extMatch = urlPath.match(/\.(png|jpe?g|gif|webp|bmp)$/i);
        const ext = extMatch ? extMatch[1].toLowerCase().replace('jpeg', 'jpg') : 'jpg';
        const fileName = `${safeUnitCode}_${now}_${index}.${ext}`;
        const s3Key = `mathtailer/board/images/${fileName}`;

        const response = await fetch(`${fnc.svrUrl}/s3Routes/uploadFromUrl`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url, s3Key })
        });

        if (!response.ok) throw new Error('External image upload to S3 failed');
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'External image upload to S3 failed');
        return s3Key;
    };

    const normalizeContentImageSrcToS3PathAsync = async (html) => {
        if (!html) return '';

        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const imgNodes = Array.from(doc.querySelectorAll('img[src]'));

            for (let i = 0; i < imgNodes.length; i += 1) {
                const node = imgNodes[i];
                const src = node.getAttribute('src') || '';

                if (/^data:image\//i.test(src)) {
                    const uploadedKey = await uploadDataImageToS3(src, i);
                    node.setAttribute('src', uploadedKey);
                    continue;
                }

                const s3Key = extractS3KeyFromSrc(src);
                if (s3Key) {
                    node.setAttribute('src', s3Key);
                    continue;
                }

                // 외부 HTTP/HTTPS URL (S3 아닌 것) → 서버 경유로 S3 업로드
                if (/^https?:\/\//i.test(src)) {
                    try {
                        const uploadedKey = await uploadExternalImageToS3(src, i);
                        node.setAttribute('src', uploadedKey);
                    } catch (err) {
                        console.error('External image upload failed for:', src, err);
                        // 업로드 실패 시 원래 src 유지 → blocked 체크에서 걸림
                    }
                }
            }

            return doc.body.innerHTML;
        } catch (error) {
            return normalizeContentImageSrcToS3Path(html);
        }
    };

    const isBlockedManualImageSrc = (src) => {
        if (!src) return true;
        const trimmed = String(src).trim();
        if (!trimmed) return true;
        if (/^(data:|blob:|javascript:|mailto:|tel:)/i.test(trimmed)) return true;
        return !extractS3KeyFromSrc(trimmed);
    };

    const findBlockedManualImageSources = (html) => {
        if (!html) return [];

        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const imgNodes = Array.from(doc.querySelectorAll('img[src]'));
            const blocked = imgNodes
                .map((node) => node.getAttribute('src') || '')
                .filter((src) => isBlockedManualImageSrc(src));

            return Array.from(new Set(blocked));
        } catch (error) {
            return [];
        }
    };

    useEffect(() => {
        if (selectedCategory) {
            setFormData(prev => ({
                ...prev,
                pCode: selectedCategory.unit1_code
            }));
            loadList(selectedCategory.unit1_code);
        }
    }, [selectedCategory]);

    const loadList = (pCode) => {
        setLoading(true);
        fnc.sendAxios('/boardRoutes/select_board_unit2', { pCode, showAll: true }, (res) => {
            if (res && Array.isArray(res)) {
                const newList = res.map(item => ({
                    sno: item["sno"],
                    pCode: item["pCode"],
                    unit2_code: item["unit2_code"],
                    unit2_name: item["unit2_name"],
                    sort_order: item["sort_order"],
                    useyn: item["useyn"] || 'Y'
                }));
                setList(newList);
            }
            setLoading(false);
        });
    };

    const handleCreate = () => {
        setMode('create');
        const newUnit2Code = `${selectedCategory.unit1_code}_${Date.now()}`;
        setFormData({
            sno: null,
            pCode: selectedCategory.unit1_code,
            unit2_code: newUnit2Code,
            unit2_name: '',
            content: '',
            sort_order: list.length + 1,
            useyn: 'Y'
        });
        setAttachments([]);
    };



    const handleEdit = (item) => {
        setMode('edit');
        // content는 별도 API로 조회
        fnc.sendAxios('/boardRoutes/select_board_content', { unit2_code: item.unit2_code }, (res) => {
            const content = (res && res.length > 0) ? res[0].content : '';

            normalizeContentImageSrcToSignedUrlAsync(content).then((viewerContent) => {
                setFormData({
                    sno: item.sno,
                    pCode: item.pCode,
                    unit2_code: item.unit2_code,
                    unit2_name: item.unit2_name,
                    content: viewerContent || content,
                    sort_order: item.sort_order || 0,
                    useyn: item.useyn || 'Y'
                });
            });
            
            // 첨부파일 목록 조회
            fnc.sendAxios('/boardRoutes/select_boardFile', { boardCode: item.unit2_code }, (fileRes) => {
                if (fileRes && Array.isArray(fileRes)) {
                    const files = fileRes.map(f => ({
                        sno: f.sno,
                        name: f.fileName2 || f.fileName,
                        s3Key: f.fileName,
                        size: 0, // DB에 저장되지 않은 경우
                        uploadDate: f.sDate,
                        fileType: f.fileType
                    }));
                    setAttachments(files);
                } else {
                    setAttachments([]);
                }
            });
        });
    };

    const handleDelete = (item) => {
        if (!window.confirm(`"${item.unit2_name}"을(를) 삭제하시겠습니까?`)) {
            return;
        }

        fnc.sendAxios('/boardRoutes/delete_board_unit2', { sno: item.sno, unit2_code: item.unit2_code }, (res) => {
            if (res && res.success) {
                alert('삭제되었습니다.');
                loadList(selectedCategory.unit1_code);
                onSave?.();
            } else {
                alert('삭제에 실패했습니다.');
            }
        });
    };

    const handleSave = async () => {
        if (!formData.unit2_name.trim()) {
            alert('제목을 입력해주세요.');
            return;
        }

        let normalizedContent = '';
        try {
            normalizedContent = await normalizeContentImageSrcToS3PathAsync(formData.content);
        } catch (error) {
            alert('본문 이미지를 S3로 업로드하는 중 오류가 발생했습니다. 다시 시도해주세요.');
            return;
        }

        const blockedImageSources = findBlockedManualImageSources(normalizedContent);
        if (blockedImageSources.length > 0) {
            const previewList = blockedImageSources.slice(0, 5).join('\n');
            const moreText = blockedImageSources.length > 5
                ? `\n...외 ${blockedImageSources.length - 5}건`
                : '';

            alert(
                `S3 경로가 아닌 이미지 URL이 포함되어 저장할 수 없습니다.\n` +
                `이미지를 에디터/파일 업로드로 다시 넣어주세요.\n\n` +
                `${previewList}${moreText}`
            );
            return;
        }

        const endpoint = mode === 'create' 
            ? '/boardRoutes/insert_board_unit2' 
            : '/boardRoutes/update_board_unit2';

        const payload = {
            ...formData,
            content: normalizedContent
        };

        fnc.sendAxios(endpoint, payload, (res) => {
            if (res && res.success) {
                alert(mode === 'create' ? '등록되었습니다.' : '수정되었습니다.');
                setMode('list');
                loadList(selectedCategory.unit1_code);
                onSave?.();
            } else {
                alert('저장에 실패했습니다.');
            }
        });
    };



    const handleCancel = () => {
        setMode('list');
        setFormData({
            sno: null,
            pCode: selectedCategory.unit1_code,
            unit2_code: '',
            unit2_name: '',
            content: '',
            sort_order: 0,
            useyn: 'Y'
        });
        setAttachments([]);
    };

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // 파일 크기 체크 (10MB 제한 - nginx 서버 설정에 맞춤)
        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
        const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE);
        
        if (oversizedFiles.length > 0) {
            const fileNames = oversizedFiles.map(f => `${f.name} (${formatFileSize(f.size)})`).join('\n');
            alert(`다음 파일들의 크기가 ${formatFileSize(MAX_FILE_SIZE)}를 초과합니다:\n\n${fileNames}\n\n서버 설정상 ${formatFileSize(MAX_FILE_SIZE)} 이하의 파일만 업로드 가능합니다.`);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        setUploading(true);
        const totalFiles = files.length;
        let successCount = 0;
        const failedFiles = [];

        try {
            for (const file of files) {
                try {
                    await uploadFileToS3(file);
                    successCount++;
                } catch (error) {
                    failedFiles.push({ name: file.name, error: error.message });
                }
            }

            if (failedFiles.length === 0) {
                alert(`모든 파일(${successCount}개)이 성공적으로 업로드되었습니다.`);
            } else {
                const failedList = failedFiles.map(f => `- ${f.name}: ${f.error}`).join('\n');
                alert(`업로드 완료:\n성공: ${successCount}개\n실패: ${failedFiles.length}개\n\n실패한 파일:\n${failedList}\n\n※ 서버 설정상 ${formatFileSize(MAX_FILE_SIZE)} 이하 파일만 업로드 가능합니다.\n※ 관리자에게 nginx client_max_body_size 설정을 확인하세요.`);
            }
        } catch (error) {
            console.error('파일 업로드 오류:', error);
            alert(`파일 업로드에 실패했습니다: ${error.message}`);
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const uploadFileToS3 = async (file) => {
        const timestamp = Date.now();
        const s3Key = `mathtailer/board/files/${formData.unit2_code}_${timestamp}_${file.name}`;
        
        const formDataToSend = new FormData();
        formDataToSend.append('file', file);
        formDataToSend.append('s3Key', s3Key);

        return new Promise((resolve, reject) => {
            fetch(`${fnc.svrUrl}/s3Routes/uploadS3`, {
                method: 'POST',
                body: formDataToSend
            })
            .then(async res => {
                let responseData;
                const contentType = res.headers.get('content-type');
                
                // JSON 응답인지 확인
                if (contentType && contentType.includes('application/json')) {
                    responseData = await res.json();
                } else {
                    const text = await res.text();
                    try {
                        responseData = JSON.parse(text);
                    } catch {
                        responseData = { message: text };
                    }
                }
                
                if (res.ok) {
                    const fileData = {
                        boardCode: formData.unit2_code,
                        folder: 'mathtailer/board/files',
                        fileName: s3Key,
                        fileName2: file.name,
                        uid: '',
                        fileType: file.type,
                        description: ''
                    };
                    
                    fnc.sendAxios('/boardRoutes/insert_boardFile', fileData, (dbRes) => {
                        if (dbRes && dbRes.success) {
                            setAttachments(prev => [...prev, {
                                sno: dbRes.sno,
                                name: file.name,
                                s3Key: s3Key,
                                size: file.size,
                                uploadDate: new Date().toISOString(),
                                fileType: file.type
                            }]);
                            resolve();
                        } else {
                            reject(new Error('DB 저장 실패'));
                        }
                    });
                } else {
                    // 상세한 에러 정보 제공
                    console.error('S3 업로드 실패 상세:', responseData);
                    let errorMessage = `${file.name} 업로드 실패`;
                    
                    if (responseData.error) {
                        errorMessage += `: ${responseData.error}`;
                    }
                    if (responseData.code) {
                        errorMessage += ` (코드: ${responseData.code})`;
                    }
                    
                    reject(new Error(errorMessage));
                }
            })
            .catch(error => {
                console.error('네트워크 오류:', error);
                reject(new Error(`네트워크 오류 (${file.name}): ${error.message}`));
            });
        });
    };

    const handleDeleteAttachment = async (attachment) => {
        if (!window.confirm(`"${attachment.name}" 파일을 삭제하시겠습니까?`)) {
            return;
        }

        try {
            // 1. S3에서 파일 삭제
            const response = await fetch(`${fnc.svrUrl}/s3Routes/delete-upload-file`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ s3Key: attachment.s3Key })
            });

            if (response.ok && attachment.sno) {
                // 2. DB에서도 삭제
                fnc.sendAxios('/boardRoutes/delete_boardFile', { sno: attachment.sno }, (dbRes) => {
                    if (dbRes && dbRes.success) {
                        setAttachments(prev => prev.filter(att => att.s3Key !== attachment.s3Key));
                        alert('파일이 삭제되었습니다.');
                    } else {
                        alert('DB 삭제에 실패했습니다.');
                    }
                });
            } else {
                alert('파일 삭제에 실패했습니다.');
            }
        } catch (error) {
            console.error('파일 삭제 오류:', error);
            alert('파일 삭제에 실패했습니다.');
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const getEmbedUrl = (unit2Code) => `${window.location.origin}/manual/embed/${encodeURIComponent(unit2Code)}`;

    const copyEmbedLink = async (item) => {
        const url = getEmbedUrl(item.unit2_code);

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = url;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }

            alert('링크를 복사했습니다. 파이선에 붙여넣으세요.');
        } catch (error) {
            alert(`링크 복사에 실패했습니다.\n${url}`);
        }
    };

    const handleToggleUseyn = (item) => {
        const updated = {
            sno: item.sno,
            pCode: item.pCode,
            unit2_code: item.unit2_code,
            unit2_name: item.unit2_name,
            sort_order: item.sort_order,
            useyn: item.useyn === 'Y' ? 'N' : 'Y'
        };
        fnc.sendAxios('/boardRoutes/update_board_unit2', updated, (res) => {
            if (res && res.success) loadList(selectedCategory.unit1_code);
        });
    };

    const handleMoveUp = (item, index) => {
        if (index === 0) return; // 첫 번째 항목은 위로 이동 불가
        
        const currentItem = {
            sno: item.sno,
            pCode: item.pCode,
            unit2_code: item.unit2_code,
            unit2_name: item.unit2_name,
            sort_order: item.sort_order - 1
        };
        const prevItem = {
            sno: list[index - 1].sno,
            pCode: list[index - 1].pCode,
            unit2_code: list[index - 1].unit2_code,
            unit2_name: list[index - 1].unit2_name,
            sort_order: list[index - 1].sort_order + 1
        };

        // 두 항목의 순서를 업데이트
        Promise.all([
            new Promise((resolve) => {
                fnc.sendAxios('/boardRoutes/update_board_unit2', currentItem, resolve);
            }),
            new Promise((resolve) => {
                fnc.sendAxios('/boardRoutes/update_board_unit2', prevItem, resolve);
            })
        ]).then(() => {
            loadList(selectedCategory.unit1_code);
            onSave?.();
        });
    };

    const handleMoveDown = (item, index) => {
        if (index === list.length - 1) return; // 마지막 항목은 아래로 이동 불가
        
        const currentItem = {
            sno: item.sno,
            pCode: item.pCode,
            unit2_code: item.unit2_code,
            unit2_name: item.unit2_name,
            sort_order: item.sort_order + 1
        };
        const nextItem = {
            sno: list[index + 1].sno,
            pCode: list[index + 1].pCode,
            unit2_code: list[index + 1].unit2_code,
            unit2_name: list[index + 1].unit2_name,
            sort_order: list[index + 1].sort_order - 1
        };

        // 두 항목의 순서를 업데이트
        Promise.all([
            new Promise((resolve) => {
                fnc.sendAxios('/boardRoutes/update_board_unit2', currentItem, resolve);
            }),
            new Promise((resolve) => {
                fnc.sendAxios('/boardRoutes/update_board_unit2', nextItem, resolve);
            })
        ]).then(() => {
            loadList(selectedCategory.unit1_code);
            onSave?.();
        });
    };

    const config = useMemo(
        () => ({
            readonly: false,
            placeholder: '',
            colorPickerDefaultTab: 'color',
            imageDefaultWidth: 'auto',
            uploader: {
                insertImageAsBase64URI: true,
                imagesExtensions: ["jpg", "png", "jpeg", "gif"],
            },
            height: 'calc(100vh - 200px)',
            minHeight: 500,
        }),
        []
    );

    if (!selectedCategory) {
        return (
            <div className="MaualAdmin">
                <div className="empty-state">
                    <p>좌측 메뉴에서 카테고리를 선택해주세요.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="MaualAdmin">
            <div className="admin-header">
                <h2>{selectedCategory.unit1_name} 관리</h2>
                <button className="btn-close" onClick={onClose}><IoClose /></button>
            </div>

            {mode === 'list' && (
                <div className="admin-content">
                    <div className="content-header">
                        <h3>목록</h3>
                        <button className="btn-create" onClick={handleCreate}>
                            + 새글 작성
                        </button>
                    </div>

                    {loading ? (
                        <div className="loading">로딩 중...</div>
                    ) : (
                        <div className="item-list">
                            {list.length === 0 ? (
                                <div className="empty-message">등록된 항목이 없습니다.</div>
                            ) : (
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>No</th>
                                            <th>제목</th>
                                            <th>순서</th>
                                            <th>순서변경</th>
                                            <th>사용</th>
                                            <th>링크</th>
                                            <th>관리</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {list.map((item, index) => (
                                            <tr key={item.sno}>
                                                <td>{index + 1}</td>
                                                <td className="title-cell">{item.unit2_name}</td>
                                                <td>{item.sort_order}</td>
                                                <td>
                                                    <button 
                                                        className="btn-order"
                                                        onClick={() => handleMoveUp(item, index)}
                                                        disabled={index === 0}
                                                    >
                                                        ▲
                                                    </button>
                                                    <button 
                                                        className="btn-order"
                                                        onClick={() => handleMoveDown(item, index)}
                                                        disabled={index === list.length - 1}
                                                    >
                                                        ▼
                                                    </button>
                                                </td>
                                                <td>
                                                    <button
                                                        className={`btn-useyn ${item.useyn === 'Y' ? 'active' : 'inactive'}`}
                                                        onClick={() => handleToggleUseyn(item)}
                                                    >
                                                        {item.useyn === 'Y' ? 'Y' : 'N'}
                                                    </button>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn-copy-link"
                                                        onClick={() => copyEmbedLink(item)}
                                                        title="공개 링크 복사"
                                                    >
                                                        <FiLink />
                                                        링크 복사
                                                    </button>
                                                </td>
                                                <td>
                                                    <div className="btn-action-group">
                                                        <button 
                                                            className="btn-edit"
                                                            onClick={() => handleEdit(item)}
                                                        >
                                                            수정
                                                        </button>
                                                        <button 
                                                            className="btn-delete"
                                                            onClick={() => handleDelete(item)}
                                                        >
                                                            삭제
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            )}

            {(mode === 'create' || mode === 'edit') && (
                <div className="admin-content">
                    <div className="form-title-header">
                        {mode === 'edit' && <h3>글 수정</h3>}
                    </div>
                    <div className="form-header">
                        <div className="input-row">
                            <span className="inline-label">제목</span>
                            <input
                                type="text"
                                value={formData.unit2_name}
                                onChange={(e) => setFormData({...formData, unit2_name: e.target.value})}
                                placeholder="제목을 입력하세요"
                                className="title-input"
                            />
                        </div>
                        <div className="input-row">
                            <span className="inline-label">순서</span>
                            <input
                                type="number"
                                value={formData.sort_order}
                                onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                                className="order-input"
                            />
                        </div>
                        <div className="input-row">
                            <span className="inline-label">사용여부</span>
                            <button
                                type="button"
                                className={`btn-useyn ${formData.useyn === 'Y' ? 'active' : 'inactive'}`}
                                onClick={() => setFormData({...formData, useyn: formData.useyn === 'Y' ? 'N' : 'Y'})}
                            >
                                {formData.useyn === 'Y' ? '사용 (Y)' : '미사용 (N)'}
                            </button>
                        </div>
                        <div className="btn-group-right">
                            <button className="btn-cancel" onClick={handleCancel}>
                                취소
                            </button>
                            <button className="btn-save" onClick={handleSave}>
                                저장
                            </button>
                        </div>
                    </div>

                    <div className="form-container">

                        <div className="editor-container">
                            <JoditEditor
                                ref={editor}
                                value={formData.content}
                                config={config}
                                onBlur={(newContent) => setFormData({...formData, content: newContent})}
                            />
                        </div>

                        <div className="form-row full">
                            <div className="attachment-section">
                                <div className="upload-area">
                                    <span className="inline-label">첨부파일</span>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        onChange={handleFileSelect}
                                        style={{ display: 'none' }}
                                        id="file-upload"
                                    />
                                    <button
                                        type="button"
                                        className="btn-upload"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                    >
                                        <FiUpload />
                                        {uploading ? '업로드 중...' : '파일 선택'}
                                    </button>
                                    <span className="upload-hint">여러 파일을 동시에 선택할 수 있습니다</span>
                                </div>

                                {attachments.length > 0 && (
                                    <div className="attachment-list">
                                        {attachments.map((att, index) => (
                                            <div key={index} className="attachment-item">
                                                <FiFile className="file-icon" />
                                                <div className="file-info">
                                                    <span className="file-name">{att.name}</span>
                                                    <span className="file-size">{formatFileSize(att.size)}</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn-delete-file"
                                                    onClick={() => handleDeleteAttachment(att)}
                                                    title="삭제"
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="attachment-actions">
                                    <button className="btn-cancel" onClick={handleCancel}>
                                        취소
                                    </button>
                                    <button className="btn-save" onClick={handleSave}>
                                        저장
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            )}

        </div>
    );
};

export default MaualAdmin;
